import { ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import {
  Content,
  Header,
  HeaderContainer,
  HeaderGlobalBar,
  HeaderMenuButton,
  HeaderName,
  SideNav,
  SideNavItems,
  SkipToContent,
} from "@carbon/react";
import "./appShell.scss";

interface AppShellProps {
  /** Navigation items, from AppShellNav. */
  nav?: ReactNode;
  /** Controls rendered on the right of the header. */
  globalActions?: ReactNode;
  children: ReactNode;
}

/**
 * `@carbon/react` only re-exports `HeaderContainerProps` (the wrapper's own
 * props), not the shape it hands to `render` — that one lives on an internal
 * type (`HeaderContainerRenderProps`) that isn't part of the package's
 * public d.ts. Declared locally rather than importing from an internal path.
 */
interface HeaderContainerRenderArgs {
  isSideNavExpanded: boolean;
  onClickSideNavExpand: () => void;
}

interface ShellBodyProps extends HeaderContainerRenderArgs {
  nav?: ReactNode;
  globalActions?: ReactNode;
  children: ReactNode;
}

/**
 * The actual chrome + content, rendered through `HeaderContainer`'s `render`
 * prop.
 *
 * Declared as a stable, module-scope component rather than inline inside
 * `AppShell` — this matters, not just style. `HeaderContainer` renders
 * whatever it receives as `render` as a component: `jsx(Children, props)`.
 * An inline arrow function is a *new* value on every `AppShell` render, so
 * React would treat each one as a distinct component type and remount this
 * entire subtree (Header, SideNav, Content — losing SideNav's hover state,
 * hard-resetting DOM) every time `AppShell` re-rendered. That was latent
 * before (nothing made `AppShell` re-render), but the route-change effect
 * below needs `useLocation()`, and calling it in `AppShell` itself would
 * make every navigation re-render `AppShell` and thus remount this whole
 * subtree via that inline-function trap. Calling `useLocation()` here
 * instead means only this already-stable component re-renders on
 * navigation — a normal update, not a remount.
 */
const ShellBody = ({
  nav,
  globalActions,
  children,
  isSideNavExpanded,
  onClickSideNavExpand,
}: ShellBodyProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);

  /**
   * Collapses the rail whenever the route changes while it is pinned open.
   *
   * The deleted `MobileMenu` closed itself on every link click. Carbon's
   * `SideNavLink` attaches no such handler, and on touch there is no
   * `mouseleave` and the tapped link keeps focus, so `onSideNavBlur` never
   * fires either — without this, the route changes behind a still-open nav
   * and an active overlay.
   */
  useEffect(() => {
    // Skip the mount run — there is no "change" yet, and firing here could
    // collapse a nav the user just opened before navigating anywhere.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Desktop (lg and up) keeps the rail pinned across navigation — it is
    // open by default now and the user closes it deliberately, so it must
    // persist. Below lg the expanded nav is a full-screen overlay (Carbon's
    // own behaviour), which must still close after the user taps a link.
    const isOverlayWidth = window.matchMedia("(max-width: 1055.98px)").matches;
    if (isOverlayWidth && isSideNavExpanded) {
      onClickSideNavExpand();

      // Carbon's rail additionally drives its own internal hover/focus
      // "preview" expand state (SideNav.js's onFocus handler on the <nav>:
      // focusing anything inside it while isRail sets that state true) —
      // separate from, and not reachable through, our controlled `expanded`
      // prop. The just-tapped link is still focused at this point (that's
      // exactly the "no mouseleave, tapped link keeps focus" problem this
      // effect exists for), so without this the rail stays visually pinned
      // open even though onClickSideNavExpand() above has already
      // collapsed our own state. Blurring it triggers SideNav's onBlur
      // handler, which clears that internal state too.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.closest(".cds--side-nav")) {
        active.blur();
      }
    }
    // Only the route is the trigger; isSideNavExpanded/onClickSideNavExpand
    // are read at effect-run time via the closure from the render that
    // produced this pathname, not tracked as dependencies — depending on
    // them would also re-fire this on every open/close toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      <Header aria-label="Simbuy" className="app-shell">
        <SkipToContent />
        {/*
          `isCollapsible` is required here. Without it, HeaderMenuButton
          applies Carbon's `header__menu-toggle__hidden` class, which is
          `display: none` at the `lg` breakpoint and above (see
          @carbon/styles _header.scss and HeaderMenuButton.js). That would
          leave desktop users with no visible way to toggle the rail
          open/closed — only a hover-to-preview, which is invisible to
          keyboard and touch. `isCollapsible` keeps the button visible and
          clickable at every width.
        */}
        <HeaderMenuButton
          aria-label={
            isSideNavExpanded ? t("navigation.close") : t("navigation.open")
          }
          onClick={onClickSideNavExpand}
          isActive={isSideNavExpanded}
          isCollapsible
          aria-expanded={isSideNavExpanded}
        />
        {/*
          Restores the deleted Navbar's behaviour of taking users home when
          they click the logo/name. HeaderName spreads its rest props into
          Carbon's UIShell `Link`, which resolves `BaseComponent ?? element
          ?? "a"` from `as` — the same mechanism AppShellNav uses for
          SideNavLink. Without `as`, HeaderName renders an `<a>` with no
          `href`: not focusable, not a link.
        */}
        <HeaderName prefix="" as={Link} to="/">
          Simbuy
        </HeaderName>
        {globalActions ? (
          <HeaderGlobalBar>{globalActions}</HeaderGlobalBar>
        ) : null}
        <SideNav
          aria-label={t("navigation.sidebar")}
          isRail
          expanded={isSideNavExpanded}
          onOverlayClick={onClickSideNavExpand}
          onSideNavBlur={onClickSideNavExpand}
        >
          <SideNavItems>{nav}</SideNavItems>
        </SideNav>
      </Header>
      {/*
        `id="main-content"` gives SkipToContent's default
        `href="#main-content"` an actual target — without it the first tab
        stop on every page was a link to nowhere.
        `app-shell__content` is what appShell.scss uses to set the real
        inline-start offset: Carbon's own sibling-combinator rules for this
        (`.cds--side-nav ~ .cds--content`) can never match here, because
        SideNav renders *inside* Header, not as Content's sibling. Header
        and Content must stay adjacent DOM siblings for that rule to work,
        which is why this returns a Fragment rather than a wrapping element.
      */}
      <Content className="app-shell__content" id="main-content">
        {children}
      </Content>
    </>
  );
};

/**
 * The application frame: dark Carbon header, collapsible rail, content slot.
 *
 * HeaderContainer owns the expanded state, so nothing above this component
 * needs to track it — that is why Layout no longer has `sidebarOpen`.
 */
const AppShell = ({ nav, globalActions, children }: AppShellProps) => (
  <HeaderContainer
    isSideNavExpanded
    nav={nav}
    globalActions={globalActions}
    render={ShellBody}
  >
    {children}
  </HeaderContainer>
);

export default AppShell;
